import React from 'react';
import { Player } from '../types';
import { Heart } from 'lucide-react';

interface PlayerPanelProps {
  player: Player;
  isActive: boolean;
  maxHealth?: number;
}

// 主题类名写成完整字面量：Tailwind 只扫描源码里出现的完整类名，
// `border-${player.color}-500` 这种拼法一旦别处没有同样字面量就会静默失效。
const PLAYER_THEMES: Record<string, {
  activeCard: string;
  heart: string;
  turnLabel: string;
  attemptBar: string;
}> = {
  red: {
    activeCard: 'border-red-500 bg-red-900/20',
    heart: 'fill-red-500 text-red-500',
    turnLabel: 'text-red-400',
    attemptBar: 'bg-red-500',
  },
  blue: {
    activeCard: 'border-blue-500 bg-blue-900/20',
    heart: 'fill-blue-500 text-blue-500',
    turnLabel: 'text-blue-400',
    attemptBar: 'bg-blue-500',
  },
};

const PlayerPanel: React.FC<PlayerPanelProps> = ({ player, isActive, maxHealth = 3 }) => {
  const theme = PLAYER_THEMES[player.color] ?? PLAYER_THEMES.red;

  return (
    <>
    <div 
      className={`
        flex flex-col items-center justify-center p-2 md:p-4 rounded-xl border-2 transition-all duration-300 w-full sm:w-64
        ${isActive 
          ? `${theme.activeCard} shadow-[0_0_10px_rgba(0,0,0,0.5)] md:shadow-[0_0_20px_rgba(0,0,0,0.5)] scale-100 md:scale-105`
          : 'border-neutral-800 bg-neutral-900/50 opacity-60'
        }
      `}
    >
      <h2 className={`text-xs md:text-2xl font-orbitron font-bold tracking-wider mb-1 md:mb-2 whitespace-nowrap overflow-hidden text-ellipsis w-full text-center ${isActive ? 'text-white' : 'text-neutral-500'}`}>
        {player.name}
      </h2>

      {/* Health indicator (heart shape) */}
      <div className="flex gap-1 md:gap-2 mb-1 md:mb-2 flex-wrap justify-center max-w-[120px] md:max-w-[200px]">
        {Array.from({ length: maxHealth }).map((_, i) => (
          <Heart
            key={i}
            className={`w-4 h-4 md:w-6 md:h-6 transition-all duration-300 
              ${i < player.health 
                ? theme.heart
                : 'fill-transparent text-neutral-700'}`}
          />
        ))}
      </div>

      {isActive && (
        <div className={`mt-1 md:mt-3 text-[9px] md:text-xs uppercase tracking-[0.2em] animate-pulse ${theme.turnLabel} hidden md:block`}>
          Current Turn
        </div>
      )}
    </div>

    {/* Attempts: three bars with small gaps below the panel */}
    <div className="w-full sm:w-64 mt-2 md:mt-3 flex gap-0.5 md:gap-1">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className={`flex-1 h-1 md:h-1.5 rounded-sm transition-colors duration-300
            ${i < player.attemptsLeft
              ? (isActive ? theme.attemptBar : 'bg-neutral-700')
              : 'bg-neutral-800/60'}`}
        />
      ))}
    </div>
    </>
  );
};

export default PlayerPanel;
